/**
 * Die Stimme des Schmieds im Gerät. (15.10.2026, shared/schmied.ts)
 *
 * Was app.ts zum Vorlesen eines Beitrags braucht, ohne Gerät rein prüfbar:
 *   - `schmiedVorlesenGilt`: wird DIESER Beitrag vorgelesen? Nur bei „Entwickler vorlesen“ (Konto 1, Vorgabe aus), mit
 *     eingeschaltetem Lautsprecher und nur, wenn er frisch ist (ein Abgleich nach einem Abriss liest Altes nicht vor);
 *   - `schmiedTtsAnfrage`: der Rumpf an /api/voice-preview — die Stimme des Schmieds (Soniox), nie Vesperas Vorlese-Stimme,
 *     und sein eigenes Tempo;
 *   - `schmiedSchlange`: eine Vorlese-Schlange (lib/tts-queue.ts) mit Sprecher „schmied“ — ihr Ende entscheidet das Mikro
 *     nicht (app.ts), das wartende Mikro erfährt es über lib/schmied-warten.ts. Vertont wird über Vesperas Weg
 *     (app.ts `fetchTtsChunk` mit eigener Stimme), mit denselben Schaltern — für den Knopf an der Blase wie fürs Vorlesen.
 *   - `schmiedGanzGilt` / `SCHMIED_GANZ_MAX_MS` (16.10.2026): ein Beitrag wird erst ganz vertont, dann in einem Zug gesprochen.
 */
import { SCHMIED_NAME, entwicklerStimmeAus, entwicklerTempoAus, entwicklerVorlesenAn, schmiedStimmeAus, zusatzVorlesenAn } from "../../shared/schmied.js";
import { entwickler, type EntwicklerId } from "../../shared/entwickler.js";
import { leseStimmeKennung } from "../../shared/stimme-konto.js";
import { ANDERSWO_GESPROCHEN, TtsQueue } from "./tts-queue.js";

export { ANDERSWO_GESPROCHEN };

/**
 * Die Kennung eines Beitrags für die Abstimmung zwischen Seiten (17.10.2026, seiten-abgleich.ts „Geparkt“): jede Seite
 * bekommt denselben Beitrag über den Bus — mit derselben Sitzung (server/dreier-routen.ts: eine eigene je Beitrag). Ohne
 * gültige Sitzung der Zeitstempel. Ohne Inhalt. `undefined`: keine Kennung (dann verwirft eine nicht vordere Seite wie bisher). Rein.
 */
export function schmiedBeitragKennung(sessionId: unknown, createdAt: unknown): string | undefined {
  const sitzung = leseStimmeKennung(typeof sessionId === "string" ? `schmied:${sessionId}` : null);
  if (sitzung) return sitzung;
  const ms = typeof createdAt === "string" ? Date.parse(createdAt) : NaN;
  return Number.isFinite(ms) ? `schmied:t${ms}` : undefined;
}

/** Ein Beitrag, der älter ist (Abgleich nach einem Abriss), wird nicht mehr vorgelesen — und öffnet als Frage kein Mikro (17.10.2026). */
export const SCHMIED_FRISCH_MS = 120_000;

/** Ist der Beitrag frisch (höchstens SCHMIED_FRISCH_MS alt)? Ohne lesbares `createdAt` gilt er als frisch. Rein. */
export function schmiedBeitragFrisch(createdAt: string | undefined, jetzt: number): boolean {
  const alter = createdAt ? jetzt - Date.parse(createdAt) : 0;
  return !(alter > SCHMIED_FRISCH_MS);
}

/** Wird dieser Beitrag vorgelesen? Rein. */
export function schmiedVorlesenGilt(o: {
  /** Der Schalter „Entwickler vorlesen“ (roh aus den Einstellungen). */
  wert: unknown;
  uid: number | null | undefined;
  /** Der Lautsprecher (Antworten vorlesen). */
  lautsprecher: boolean;
  /** Wann der Beitrag gespeichert wurde (ISO); fehlt er, gilt er als frisch. */
  createdAt?: string;
  jetzt: number;
}): boolean {
  if (!o.lautsprecher || !entwicklerVorlesenAn(o.wert, o.uid)) return false;
  return schmiedBeitragFrisch(o.createdAt, o.jetzt);
}

/**
 * Wird dieser Beitrag von Zimmermann oder Hüter von selbst vorgelesen? (19.10.2026) NUR im Gespräch (`imGespraech`: der Server sagt es am
 * Beitrag — Faden offen oder Übergabe), mit eigenem Schalter (Vorgabe an), eingeschaltetem Lautsprecher und frisch. Nimmt Martin gerade auf,
 * ist das KEIN Grund zu verwerfen (Martin, 19.10.2026): die App merkt den Beitrag vor und spricht nach der Aufnahme (app.ts `sprichSchmied`
 * wartet auf `schmiedStilleBesetzt`), wenn er dann noch frisch ist — dafür gilt dieselbe Prüfung ein zweites Mal. Ein Bericht im Hintergrund bleibt Text. Rein.
 */
export function zusatzVorlesenGilt(o: {
  imGespraech: boolean;
  /** Der Schalter „Zimmermann und Hüter im Gespräch vorlesen“ (roh aus den Einstellungen; fehlt = an). */
  wert: unknown;
  uid: number | null | undefined;
  lautsprecher: boolean;
  createdAt?: string;
  jetzt: number;
}): boolean {
  if (!o.imGespraech || !o.lautsprecher || !zusatzVorlesenAn(o.wert, o.uid)) return false;
  return schmiedBeitragFrisch(o.createdAt, o.jetzt);
}

/**
 * Wird ein Bericht des Hüters oder Zimmermanns aus dem HINTERGRUND (nicht im Gespräch) von selbst vorgelesen? (20.10.2026, Martin: „Hüter steht auf
 * unvorgelesen — sollte er nicht jetzt automatisch vorlesen?“) Ja, außer er ist nur zur Info (`info`, Server `--info`): dann bleibt er
 * Text mit Knopf. Gleicher Schalter, Lautsprecher und Frische wie im Gespräch. Seit 20.10.2026 (Martin, Knopf-Ja 21:54) gilt dasselbe für Zimmermann; der Schmied bleibt wie bisher. Rein.
 */
export function hueterHintergrundVorlesenGilt(o: {
  id: string;
  /** Bleibt ohne Wirkung auf das Vorlesen (nur der Aufrufer reicht es weiter); `--info` öffnet kein Mikro. */
  info: boolean;
  wert: unknown;
  uid: number | null | undefined;
  lautsprecher: boolean;
  createdAt?: string;
  jetzt: number;
}): boolean {
  // 27.10.2026 (Martin): auch `--info` wird aus dem Hintergrund vorgelesen (Zimmermann meldet Status meist so; sonst stumm). `info` öffnet weiter nie ein Mikro.
  if (o.id !== "hueter" && o.id !== "zimmermann") return false;
  return zusatzVorlesenGilt({ imGespraech: true, wert: o.wert, uid: o.uid, lautsprecher: o.lautsprecher, createdAt: o.createdAt, jetzt: o.jetzt });
}

/** Kürzer als das ist ein reiner Info-Status („ok“, „läuft“) kein Vorlesen aus dem Hintergrund wert. */
const HINTERGRUND_INFO_MIN_ZEICHEN = 15;
const HINTERGRUND_WIEDERHOLUNG_MS = 60_000;

/**
 * Rauschen-Bremse für Hintergrund-Berichte (27.10.2026): gleicher Text vom selben Sprecher innerhalb 60 s wird nicht noch einmal gesprochen,
 * ein sehr kurzer reiner Info-Status gar nicht. Merkt sich den Text in `letzte` (Schlüssel `id`). Gesprochen wird nur bei `false`. Rein bis auf `letzte`.
 */
export function hintergrundRauschen(o: { id: string; text: string; info: boolean; letzte: Map<string, { text: string; at: number }>; jetzt: number }): boolean {
  const t = o.text.trim().replace(/\s+/g, " ");
  if (o.info && t.length < HINTERGRUND_INFO_MIN_ZEICHEN) return true;
  const vor = o.letzte.get(o.id);
  if (vor?.text === t && o.jetzt - vor.at < HINTERGRUND_WIEDERHOLUNG_MS) return true;
  o.letzte.set(o.id, { text: t, at: o.jetzt });
  return false;
}

/**
 * Öffnet die Frage eines HINTERGRUND-Berichts (nicht im Gespräch) an Martin nach dem Vorlesen das Mikro? (21.10.2026, Martin 07:14) Nur bei
 * Zimmermann — der Hüter bleibt wie bisher: vorgelesen, aber nie ein Mikro aus dem Hintergrund (Prüfauflage). Rein.
 */
export function hintergrundFrageOeffnetMikro(id: string): boolean {
  return id === "zimmermann";
}

/**
 * Hindert etwas den Schmied am Sprechen? Rein. (16.10.2026, Martin: „der Schmied sprach in meine Aufnahme hinein“)
 * Eine Stimme klingt (Vespera, Sprechtaste) ODER Martin nimmt gerade auf (Sprechtaste/Auto-Mikro, auch im Anlauf).
 * Die Tagesaufnahme läuft dauernd und ist KEIN Grund — sie kommt hier nicht vor.
 */
export function schmiedStilleBesetzt(o: { stimmeKlingt: boolean; sprechtasteSpricht: boolean; aufnahme: boolean }): boolean {
  return o.stimmeKlingt || o.sprechtasteSpricht || o.aufnahme;
}

/** Wartet, bis `besetzt` falsch ist (Takt 100 ms). `false`, wenn die Frist vorher abläuft. */
export async function schmiedWarteAufStille(besetzt: () => boolean, maxMs: number): Promise<boolean> {
  const bis = Date.now() + maxMs;
  while (besetzt()) {
    if (Date.now() > bis) return false;
    await new Promise((r) => globalThis.setTimeout(r, 100));
  }
  return true;
}

/**
 * Der Text der Sprech-Pille, solange eine Schlange nur auf den ANDEREN Sprecher wartet (17.10.2026, Gutachten: sie sagte
 * „Stimme wird vorbereitet“, obwohl nichts vorbereitet wurde). Kurz, ohne Inhalte. Rein.
 */
export function wartetAufText(eigenerSprecher: "schmied" | "probe" | undefined): string {
  // 17.10.2026: die Hörprobe („probe“, lib/vespera-satz.ts) wartet wie der Schmied auf Vespera.
  return `Wartet auf ${eigenerSprecher === "schmied" || eigenerSprecher === "probe" ? "Vespera" : SCHMIED_NAME} \u2026`;
}

/** Der Rumpf an /api/voice-preview für einen Satz des Schmieds. Rein. */
export function schmiedTtsAnfrage(text: string, gewaehlt: unknown, gewaehltTempo: unknown = null): { voiceId: string; text: string; speakingRate: number } {
  // 17.10.2026: das Tempo ist Martins Kontowert `entwicklerTempo`; ohne gültigen Wert gilt der bisherige Festwert.
  return { voiceId: schmiedStimmeAus(gewaehlt), text, speakingRate: entwicklerTempoAus("schmied", gewaehltTempo) };
}

/**
 * Der Rumpf für einen Satz eines Entwicklers (16.10.2026, shared/entwickler.ts): der Schmied mit seiner gewählten Stimme (wie bisher),
 * jeder andere mit SEINER gewählten Stimme (`gewaehltEigen`, z. B. `zimmermannStimme` — Seite „Entwickler-Team“) und seinem Tempo;
 * ohne gültige Wahl seine Vorgabe aus der Liste. Das Tempo kommt aus `gewaehltTempo` (sein Kontowert, 17.10.2026), sonst sein Festwert. Rein.
 */
export function entwicklerTtsAnfrage(
  id: EntwicklerId,
  text: string,
  gewaehltSchmied: unknown,
  gewaehltEigen: unknown = null,
  gewaehltTempo: unknown = null,
): { voiceId: string; text: string; speakingRate: number } {
  if (id === "schmied") return schmiedTtsAnfrage(text, gewaehltSchmied, gewaehltTempo);
  return { voiceId: entwicklerStimmeAus(id, gewaehltEigen), text, speakingRate: entwicklerTempoAus(id, gewaehltTempo) };
}

/**
 * 16.10.2026 (Martin, 17:18: „die Stimme setzt immer wieder aus und fängt neu an … in einem Stück“): ein Beitrag wird erst
 * GANZ vertont, dann gesprochen (lib/tts-queue.ts `ganzVorab`). Dauert die Vertonung länger als das hier (ab dem ersten
 * Stück), spricht er wie bisher stückweise — lieber stückweise als gar nicht. 19.10.2026 (Martin: bis zu 2 min Stille bei langen
 * Antworten, Journal `synth_max` 28–53 s): von 25 s auf 4 s gesenkt — kurze Beiträge kommen weiter in einem Stück, lange fangen nach
 * höchstens 4 s mit dem ersten Satz an, der Rest wird währenddessen vertont. Ein Beitrag hat höchstens 2000 Zeichen; die
 * üblichen 300–450 sind bei zwei Soniox-Plätzen in rund 10 s vertont (Journal 16.10.: 2–5 s je Stück).
 */
export const SCHMIED_GANZ_MAX_MS = 4_000;

/**
 * Gilt „erst ganz vertonen“ für diesen Beitrag? Rein. Der Server hängt `stueckweise: true` an den Beitrag, solange der Kontoschalter
 * „Schmied-Stimme im Ganz-Modus“ aus ist (Vorgabe; server/dreier-routen.ts) — der Weg von vorher. Fehlt das Feld: ganz.
 */
export function schmiedGanzGilt(stueckweise: unknown): boolean {
  return stueckweise !== true;
}

/** Der Rumpf eines Satzes plus die Angabe, dass die Schlange ihn ganz vorab vertont (app.ts `fetchTtsChunk`). */
export type SchmiedTonAnfrage = ReturnType<typeof schmiedTtsAnfrage> & {
  ganz?: true;
  /** 17.10.2026: diese Anfrage lädt vorab (Ganz-Modus, Tor noch zu) — am Server ohne Vorrang. Gefragt beim Abruf. */
  vorab?: true;
};

/** Eine Vorlese-Schlange mit der Stimme des Schmieds. `holeTon` liefert die Adresse des Tons zu einem Rumpf. */
export function schmiedSchlange(o: {
  gewaehlt?: unknown;
  /** 17.10.2026: Martins gewähltes Tempo des Schmieds (`entwicklerTempo`); ohne gültigen Wert sein Festwert. */
  tempo?: unknown;
  holeTon: (anfrage: SchmiedTonAnfrage, signal: AbortSignal) => Promise<string>;
  /** Derselbe Schalter wie bei Vesperas Schlange (app.ts `beginTtsStream`): Strom-Stücke nachladen. */
  direktBeta?: () => boolean;
  onAudioStart?: () => void;
  /** 16.10.2026: erst ganz vertonen, dann sprechen (`schmiedGanzGilt`). Ohne Angabe: stückweise wie bisher. */
  ganz?: boolean;
  /** 17.10.2026: die Kennung des Beitrags (`schmiedBeitragKennung`) — nicht vorn: zurückhalten statt verwerfen. */
  kennung?: string;
  /** 19.10.2026: ein anderer Entwickler (Zimmermann, Hüter) mit SEINER gewählten Stimme (`gewaehltEigen`) und seinem Tempo (`tempo`). */
  id?: EntwicklerId;
  gewaehltEigen?: unknown;
  /**
   * 20.10.2026: die Stimme und das Tempo dieses Entwicklers, JEDES Mal frisch gefragt — dieselbe Auflösung wie am Knopf der Blase
   * (app.ts `entwicklerStimmeJetzt`). Hat sie Vorrang vor `gewaehlt`/`gewaehltEigen`/`tempo`.
   */
  stimme?: () => { voiceId: string; speakingRate: number };
}): TtsQueue {
  const queue: TtsQueue = new TtsQueue({
    fetchTts: (t, signal) =>
      o.holeTon(
        {
          ...(o.stimme ? { ...o.stimme(), text: t } : entwicklerTtsAnfrage(o.id ?? "schmied", t, o.gewaehlt, o.gewaehltEigen, o.tempo)),
          ...(o.ganz ? { ganz: true as const } : {}),
          // Datei-/Strom-Weg: beim Abruf gefragt; der PCM-Weg fragt erst beim Laden (tts-queue.ts `vorabLaedt`).
          ...(o.ganz && queue.vorabLaedt() ? { vorab: true as const } : {}),
        },
        signal,
      ),
    direktBeta: o.direktBeta,
    sprecher: "schmied",
    // 20.10.2026: die Pille nennt den, der wirklich spricht (Zimmermann, Hüter), nicht immer den Schmied.
    sprecherName: entwickler(o.id ?? "schmied").name,
    ...(o.kennung ? { kennung: o.kennung } : {}),
    onAudioStart: o.onAudioStart,
    ...(o.ganz ? { ganzVorab: { maxMs: SCHMIED_GANZ_MAX_MS } } : {}),
  });
  return queue;
}

/**
 * Der Hörer auf `voice-v8:settings-changed` für das Plus-Menü und „Entwickler vorlesen“. (16.10.2026, Prüfauflage A/B)
 *   - zeichnet den Schalter „Entwickler im Chat“ im Plus-Menü bei JEDER Änderung neu (die neue Seite, der Server-Abgleich);
 *   - stoppt einen sprechenden Schmied NUR beim Wechsel von an auf aus — nicht bei jeder anderen Änderung (Zimmermann-Stimme,
 *     Abgleich), solange der Schalter schon aus war: dann spielt ggf. der Knopf an seiner Blase, und der darf weiterspielen.
 * `vorlesenAn` liest den aktuellen Stand (Konto 1 + ausdrückliches true). Der gemerkte Stand gilt ab Bau; `merke()` setzt ihn neu,
 * sobald das Konto bekannt ist (app.ts nach der Anmeldung) — vorher sagt `vorlesenAn` ohne Konto immer „aus“.
 */
export function entwicklerEinstellungsHoerer(o: {
  vorlesenAn: () => boolean;
  zeichne: () => void;
  /** Stoppt den Schmied, falls er gerade spricht. */
  stoppeSchmied: () => void;
}): { hoere: () => void; merke: () => void } {
  let vorher = o.vorlesenAn();
  return {
    hoere: () => {
      o.zeichne();
      const jetzt = o.vorlesenAn();
      if (vorher && !jetzt) o.stoppeSchmied();
      vorher = jetzt;
    },
    merke: () => {
      vorher = o.vorlesenAn();
    },
  };
}
